import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { authAPI, BACKEND_URL } from '../services/api';
import { motion } from 'framer-motion';

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authAPI.login(formData);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('isAuthenticated', 'true');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      alert(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    window.location.href = `${BACKEND_URL}/api/google-auth/login`;
  };

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      {/* Left Hero Panel */}
      <motion.div
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
      >
        <img
          src="https://static.prod-images.emergentagent.com/jobs/bb724e77-1791-452e-9ed8-e672ab20bc64/images/3cacd19d37489e9f57a7d66e84575680a36a53a9733b4bfa696d66d2e80e430e.png"
          alt="Modern workspace"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12 pb-16 text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <h1 className="text-4xl font-medium tracking-tight leading-tight mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Manage your virtual
              <br />team, effortlessly.
            </h1>
            <p className="text-white/70 text-lg max-w-md leading-relaxed">
              Task management, timesheets, performance tracking, and billing — all in one platform built for scale.
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Right Form Panel */}
      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="flex-1 flex flex-col"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-8 py-6">
          <Link to="/" className="lg:hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_virtual-admin-1/artifacts/zwdakbm3_New%20Logo%20HD.png"
              alt="Nimble Assistants"
              className="h-10"
            />
          </Link>
          <div className="hidden lg:block" />
          <Button
            className="text-sm bg-[#5FD068] hover:bg-[#4ec057] text-white transition-colors"
            onClick={() => window.open('https://withnimble.com/contact', '_blank')}
            data-testid="free-consultation-btn"
          >
            Free Consultation
          </Button>
        </div>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center px-8 py-4">
          <div className="w-full max-w-[400px] space-y-5">
            <div>
              <h2 className="text-2xl font-medium tracking-tight text-[#0A0D0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Welcome back
              </h2>
              <p className="text-[#4B5563] mt-1 text-sm">Sign in to your account to continue</p>
            </div>

            {/* Google Sign In */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 flex items-center justify-center space-x-3 border-[#E5E7EB] hover:border-[#0A0D0A] hover:bg-[#F9FAFB] transition-all duration-200"
              onClick={handleGoogleSignIn}
              data-testid="google-signin-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="font-medium text-[#0A0D0A]">Continue with Google</span>
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-[#E5E7EB]" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-[#F9FAFB] px-4 text-xs uppercase tracking-[0.2em] text-[#4B5563] font-medium">or</span>
              </div>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium text-[#0A0D0A]">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-11 border-[#E5E7EB] focus:border-[#5FD068] focus:ring-[#5FD068] transition-colors"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-[#0A0D0A]">
                    Password
                  </Label>
                  <Link to="/forgot-password" className="text-xs text-[#4B5563] hover:text-[#5FD068] transition-colors">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="h-11 pr-10 border-[#E5E7EB] focus:border-[#5FD068] focus:ring-[#5FD068] transition-colors"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#0A0D0A] transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-[#5FD068] hover:bg-[#4BB854] text-white font-medium rounded-lg transition-all duration-200 group"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Sign In
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-[#4B5563]">
              New to Nimble Assistants?{' '}
              <Link to="/signup" className="text-[#5FD068] hover:text-[#4BB854] font-medium transition-colors">
                Create an account
              </Link>
            </p>

            <p className="text-center text-[10px] text-gray-400 leading-relaxed">
              By continuing, you agree to Nimble Assistants'{' '}
              <a href="https://withnimble.com/terms" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#5FD068] underline underline-offset-2">Terms of Service</a>
              {' '}and{' '}
              <a href="https://withnimble.com/privacy" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#5FD068] underline underline-offset-2">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
